import * as React from 'react';
import { Button, cn } from '@cguard/design-system';

/**
 * The one state overlay every player draws over its `<video>`: connecting,
 * offline, failed (with an optional Retry) and codec mismatch. A tile is
 * graphite in every theme, so text uses fixed on-dark colours rather than
 * theme foreground tokens. Copy is sentence case at 12px or more.
 */
export interface VideoStateOverlayProps {
  /**
   * - `connecting` — spinner + label, polite status.
   * - `offline` — no stream yet, polite status.
   * - `error` — playback failed; pass `onRetry` when a fresh attempt can help.
   * - `codec_mismatch` — a deployment problem, held visually heavier and never
   *   retryable: replaying the same stream cannot change its codec.
   */
  tone: 'connecting' | 'offline' | 'error' | 'codec_mismatch';
  label: string;
  /** One plain sentence under the label. Never raw player diagnostics. */
  detail?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
  testId?: string;
}

export function VideoStateOverlay({
  tone,
  label,
  detail,
  onRetry,
  retryLabel,
  testId,
}: VideoStateOverlayProps): React.JSX.Element {
  const isError = tone === 'error' || tone === 'codec_mismatch';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={cn(
        'absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center backdrop-blur-[1px]',
        tone === 'connecting'
          ? 'bg-[var(--color-graphite-950)]/70'
          : 'bg-[var(--color-graphite-950)]/80',
        tone === 'codec_mismatch' && 'bg-[var(--color-graphite-950)]/90',
      )}
      data-testid={testId}
    >
      {tone === 'connecting' ? (
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          className="animate-spin text-[var(--color-primary)]"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      ) : null}
      <span
        className={cn(
          'rounded-sm border px-2 py-1 text-xs font-medium',
          tone === 'connecting' &&
            'border-[var(--color-primary)] bg-[var(--color-canvas)]/40 text-[var(--color-primary)]',
          tone === 'offline' &&
            'border-[var(--color-slate-600)] bg-[var(--color-graphite-950)]/40 text-[var(--color-slate-300)]',
          tone === 'error' &&
            'border-[var(--color-danger)] bg-[var(--color-canvas)]/60 text-[var(--color-danger)]',
          tone === 'codec_mismatch' &&
            'border-[var(--color-danger)] bg-[var(--color-danger-tint)] text-[var(--color-danger)]',
        )}
      >
        {label}
      </span>
      {detail ? (
        <p className="max-w-[36ch] text-xs text-[var(--color-slate-300)]">{detail}</p>
      ) : null}
      {onRetry && retryLabel ? (
        <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
